/* =========================================================
   Row-level access control.

   Every list screen sees only what the active role's scope
   allows:
     ETC Admin    → everything
     Partner Mgr  → their organization(s), all their sites and
                    the instructors assigned to them
     Site Manager → their site(s) and those instructors
   ========================================================= */
import { useMemo } from 'react';
import type { Instructor, Organization, Role, SessionUser, Site } from '@/types';
import { ds, userOrgIds } from '@/data';
import { progresoDeOrg, progresoTotal, type Progreso } from '@/lib/progreso';
import { esDeUnSite } from '@/lib/roles';

export interface ScopedData {
  orgs: Organization[];
  sites: Site[];
  instr: Instructor[];
  disassociated: number;
  sitesWithRed: number;
  /** El porcentaje del proceso de actualización sobre todo lo que alcanza
   *  esta cuenta. Sumado, no promediado: un socio con seis sites pesa más
   *  que uno con uno, y el número dice cuánto trabajo queda de verdad. */
  pct: number;
  /** El detalle detrás del porcentaje, para poder enseñar «34 de 51». */
  progreso: Progreso;
}

/** True when the site still has an empty red field or a '0000' postal code. */
export function siteHasRed(s: Site): boolean {
  return !s.RegisteredPostalCode || !s.CommercialSiteName || !s.Workstations || s.SitePostalCode === '0000';
}

export function useScopedData(role: Role, user: SessionUser | null): ScopedData {
  return useMemo(() => {
    let orgs = ds.listOrgs();
    let sites = ds.listSites();
    let instr = ds.listInstructors();

    if (role === 'PARTNER_MGR') {
      const oids = userOrgIds(user);
      orgs = orgs.filter((o) => oids.includes(o.OrgId));
      sites = sites.filter((s) => oids.includes(s.OrgId));
      const aIds = ds.listAssignments().filter((a) => oids.includes(a.OrgId)).map((a) => a.InstructorId);
      instr = instr.filter((i) => aIds.includes(i.InstructorId));
    } else if (esDeUnSite(role) && user?.siteId) {
      sites = sites.filter((s) => s.SiteId === user.siteId);
      orgs = orgs.filter((o) => o.OrgId === user.orgId);
      const aIds = ds.listAssignments({ siteId: user.siteId }).map((a) => a.InstructorId);
      instr = instr.filter((i) => aIds.includes(i.InstructorId));
    }

    const disassociated = instr.filter((i) => i.disassociated).length;
    const sitesWithRed = sites.filter(siteHasRed).length;

    // El porcentaje se arma por organización y luego se suman las partes.
    // Hacerlo así y no ficha a ficha es lo que mete el compliance donde
    // corresponde: los documentos son de la organización, y contarlos una
    // vez por site los contaría cuatro veces en un socio con cuatro.
    const na = ds.marcasNA();
    const progreso = progresoTotal(orgs.map((o) => {
      const sitesDeLaOrg = sites.filter((s) => s.OrgId === o.OrgId);
      const idsDeSusSites = new Set(sitesDeLaOrg.map((s) => s.SiteId));
      // los instructores de esos sites, sin repetir a quien esté en dos
      const suyos = new Set(
        ds.listAssignments()
          .filter((a) => idsDeSusSites.has(a.SiteId))
          .map((a) => a.InstructorId),
      );
      return progresoDeOrg(
        o,
        sitesDeLaOrg,
        instr.filter((i) => suyos.has(i.InstructorId)),
        ds.getCompliance(o.OrgId),
        na,
      );
    }));

    return { orgs, sites, instr, disassociated, sitesWithRed, pct: progreso.pct, progreso };
  }, [role, user]);
}
